import type {
  BatchJob,
  FieldReannotationItemResult,
  FieldReannotationParams,
} from "../../../../../lib/corpusApi";
import { useDialogState } from "../../../../../hooks/useDialogState";
import { narrowByOutcome, type BatchJobItemRow } from "../../panel/batch_job_item_rows";
import {
  useBatchJobItemList,
  type UseBatchJobItemListResult,
} from "../../panel/useBatchJobItemList";
import {
  useBatchJobItemReview,
  type UseBatchJobItemReviewResult,
} from "../../panel/useBatchJobItemReview";
import {
  useBatchJobItemSelection,
  type BatchJobItemSelectionView,
} from "../../panel/useBatchJobItemSelection";
import { useBatchJobTabs, type UseBatchJobTabsResult } from "../../panel/useBatchJobTabs";
import { useBulkItemReview, type UseBulkItemReviewResult } from "../../panel/useBulkItemReview";
import {
  useDocumentDetailTarget,
  type UseDocumentDetailTargetResult,
} from "../../panel/useDocumentDetailTarget";
import {
  FIELD_REANNOTATION_TABS,
  type FieldReannotationTabConfig,
  type FieldReannotationTabKey,
} from "./field_reannotation_tabs";
import {
  useFieldReannotationFilters,
  type UseFieldReannotationFiltersResult,
} from "./useFieldReannotationFilters";

/** One list row: the raw item plus its field_reannotation result. A failed item has none. */
export type FieldReannotationRow = BatchJobItemRow<FieldReannotationItemResult>;

/** What the panel can do about a document whose re-annotation contradicts a person: keep their
 *  label (dismiss) or override it with the AI's (apply), one at a time or many. */
export interface FieldReannotationReview extends UseBatchJobItemReviewResult<FieldReannotationItemResult> {
  bulkApply: UseBulkItemReviewResult;
  bulkKeep: UseBulkItemReviewResult;
}

export interface UseFieldReannotationPanelResult {
  /** What the job was started with: the field it re-annotates and the scope it covers. */
  params: FieldReannotationParams;
  tabs: UseBatchJobTabsResult<FieldReannotationTabKey, FieldReannotationTabConfig>;
  filters: UseFieldReannotationFiltersResult;
  list: UseBatchJobItemListResult<FieldReannotationItemResult>;
  selection: BatchJobItemSelectionView<FieldReannotationItemResult>;
  review: FieldReannotationReview;
  detail: UseDocumentDetailTargetResult<FieldReannotationItemResult>;
  /** The AI's justification for one document's labels, before and after. */
  justifications: {
    target: FieldReannotationRow | null;
    open: (row: FieldReannotationRow) => void;
    close: () => void;
  };
}

const narrowFieldReannotationResult = narrowByOutcome<FieldReannotationItemResult>(
  new Set(["updated", "unchanged", "contradicts_human", "skipped"])
);

function toDocumentTarget(row: FieldReannotationRow) {
  return { id: row.item.subject, title: row.item.subjectTitle, url: row.item.subjectUrl };
}

function labelsText(labels: string[]): string {
  return labels.length > 0 ? labels.join(", ") : "none";
}

/** What an Override would do, in words, for its confirmation. */
function overrideMessage(row: FieldReannotationRow | null): string {
  const result = row?.result;
  if (!result?.human || !result.change) return "Replace the person's label with the AI's?";
  return `Replace the person's label (${labelsText(result.human.labels)}) with the AI's (${labelsText(result.change.after)})?`;
}

/** Drives the field_reannotation batch job panel: one paginated list per outcome, where the
 *  contradictions with a person's label can be overridden with the AI's or kept. */
export function useFieldReannotationPanel(job: BatchJob): UseFieldReannotationPanelResult {
  const params = job.params as unknown as FieldReannotationParams;

  const tabs = useBatchJobTabs(FIELD_REANNOTATION_TABS, "contradictions");
  const filters = useFieldReannotationFilters(params.field);
  const resetKey = [tabs.activeTab, filters.oldLabel, filters.newLabel].join(":");

  const list = useBatchJobItemList({
    job,
    filters: { ...tabs.tab.apiFilters, ...filters.apiFilters },
    resetKey,
    narrow: narrowFieldReannotationResult,
  });
  const selection = useBatchJobItemSelection({
    rows: list.rows,
    enabled: tabs.tab.reviewable,
    page: list.pagination.page,
    resetKey,
  });

  const single = useBatchJobItemReview({
    job,
    refresh: list.refresh,
    applyCopy: {
      title: "Override the person's label",
      message: overrideMessage,
      confirmLabel: "Override",
      confirmColor: "warning",
    },
  });
  const bulkApply = useBulkItemReview({
    jobId: job.id,
    decision: "apply",
    selection: selection.state,
    refresh: list.refresh,
    confirm: {
      title: "Override the people's labels",
      message: `Replace the person's label with the AI's on ${selection.count} document(s)? This cannot be undone.`,
      confirmLabel: "Override selected",
      confirmColor: "warning",
    },
    failureVerb: "override",
  });
  const bulkKeep = useBulkItemReview({
    jobId: job.id,
    decision: "dismiss",
    selection: selection.state,
    refresh: list.refresh,
    confirm: {
      title: "Keep the people's labels",
      message: `Keep the person's label on ${selection.count} document(s), dismissing the AI's?`,
      confirmLabel: "Keep selected",
      confirmColor: "primary",
    },
    failureVerb: "keep",
  });

  const detail = useDocumentDetailTarget(toDocumentTarget);
  const justificationsDialog = useDialogState<FieldReannotationRow>();

  return {
    params,
    tabs,
    filters,
    list,
    selection: { table: selection.table, count: selection.count },
    review: { ...single, bulkApply, bulkKeep },
    detail,
    justifications: {
      target: justificationsDialog.value,
      open: justificationsDialog.open,
      close: justificationsDialog.close,
    },
  };
}
